import { observer } from 'mobx-react-lite';
import { useEffect, useMemo, useState, useCallback, useRef } from 'react';
import { Form, Formik, useFormikContext } from 'formik';
import Popup from '../../../../components/common-components/pop-up/Popup';
import { Calendar } from 'lucide-react';
import { useStore } from '../../../../hooks/useStore';
import FormPopupFooter from '../../../../components/form-components/common-components/FormPopupFooter';
import { AppointmentSchema } from '../../../../schemas/AppointmentSchema';
import { AppointmentFormValues } from '../../../../models/appointment/AppointmentModel';
import { toast } from 'react-toastify';
import { userManagementService } from '../../../../services/modules/userManagement/userManagement.service';
import { IUserManagementResult } from '../../../../models/user-management/UserManagementModel';
import AppointmentEditFormFields from './AppointmentEditFormFields';
import styles from './NewAppointmentPopup.module.css';

// Hastane seçildiğinde kişileri yükleyen component
const PersonLoader = ({
  onHospitalChange,
}: {
  onHospitalChange: (hospitalId: number | null) => void;
}) => {
  const { values, setFieldValue } = useFormikContext<AppointmentFormValues>();
  const previousHospitalIdRef = useRef<number | null | undefined>(undefined);

  useEffect(() => {
    if (values.hospitalId !== previousHospitalIdRef.current) {
      const prev = previousHospitalIdRef.current;
      previousHospitalIdRef.current = values.hospitalId;

      if (values.hospitalId) {
        onHospitalChange(values.hospitalId);
        // Sadece kullanıcı bir hastaneden diğerine geçtiyse kişiyi sıfırla (ilk açılışta bozulmasın)
        if (typeof prev === 'number') {
          setFieldValue('personId', null);
        }
      } else {
        onHospitalChange(null);
        setFieldValue('personId', null);
      }
    }
  }, [values.hospitalId, setFieldValue, onHospitalChange]);

  return null;
};

const EditAppointmentPopup = () => {
  const { popupStore, hospitalStore, uiStore, personStore, appointmentStore } = useStore();
  const [personsFromApi, setPersonsFromApi] = useState<Array<{ value: number; label: string }>>([]);
  const [users, setUsers] = useState<IUserManagementResult[]>([]);

  const selectedAppointment = appointmentStore.selectedAppointmentToEdit;

  // İlk açılışta hemen göster: randevudaki kişi + API'den gelen liste
  const personOptions = useMemo(() => {
    const apt = selectedAppointment;
    const fromApi = [...personsFromApi];
    const pid = apt?.personId != null ? Number(apt.personId) : null;
    if (pid != null && apt?.personName && !fromApi.some(o => Number(o.value) === pid)) {
      fromApi.unshift({ value: pid, label: apt.personName });
    }
    return fromApi;
  }, [personsFromApi, selectedAppointment?.personId, selectedAppointment?.personName]);

  useEffect(() => {
    const fetchHospitals = async () => {
      if (!popupStore.isEditAppointmentOpen) return;

      try {
        uiStore.setLoading(true);
        await hospitalStore.listAllHospitals();
      } catch (err) {
        console.error('Error fetching hospitals:', err);
      } finally {
        uiStore.setLoading(false);
      }
    };
    fetchHospitals();
  }, [hospitalStore, uiStore, popupStore.isEditAppointmentOpen]);

  useEffect(() => {
    const loadPersons = async () => {
      const apt = appointmentStore.selectedAppointmentToEdit;
      if (!popupStore.isEditAppointmentOpen || !apt?.hospitalId) return;

      try {
        const persons = await personStore.getPersonsByHospitalId(apt.hospitalId);
        const opts = persons
          .filter(
            (p): p is { id: number; name: string } => p.id !== undefined && p.name !== undefined
          )
          .map(p => ({ value: p.id!, label: p.name! }));
        setPersonsFromApi(opts);
      } catch (error) {
        console.error('Error fetching persons:', error);
        setPersonsFromApi([]);
      }
    };
    loadPersons();
  }, [
    popupStore.isEditAppointmentOpen,
    appointmentStore.selectedAppointmentToEdit?.hospitalId,
    personStore,
  ]);

  useEffect(() => {
    const fetchUsers = async () => {
      if (!popupStore.isEditAppointmentOpen) return;
      try {
        const usersList = await userManagementService.getAllUsers();
        const actualUsers = Array.isArray(usersList)
          ? usersList
          : ((usersList as { value?: IUserManagementResult[] })?.value ?? []);
        setUsers(actualUsers);
      } catch (err) {
        console.error('Error fetching users:', err);
      }
    };
    fetchUsers();
  }, [popupStore.isEditAppointmentOpen]);

  const hospitalOptions = useMemo(
    () =>
      hospitalStore.hospitalList
        .filter(
          (hospital): hospital is { id: number; hospitalName: string } =>
            hospital.id !== undefined && hospital.hospitalName !== undefined
        )
        .map(hospital => ({
          value: hospital.id,
          label: hospital.hospitalName,
        })),
    [hospitalStore.hospitalList]
  );

  const handleHospitalChange = useCallback(
    async (hospitalId: number | null) => {
      if (!hospitalId) {
        setPersonsFromApi([]);
        return;
      }

      try {
        const persons = await personStore.getPersonsByHospitalId(hospitalId);
        setPersonsFromApi(
          persons
            .filter(
              (p): p is { id: number; name: string } => p.id !== undefined && p.name !== undefined
            )
            .map(p => ({
              value: p.id!,
              label: p.name!,
            }))
        );
      } catch (error) {
        console.error('Error fetching persons:', error);
        toast.error('Kişiler yüklenirken bir hata oluştu');
        setPersonsFromApi([]);
      }
    },
    [personStore]
  );

  // İstek numarası tag'den gelsin (NewAppointment / AppointmentForHospitalTag mantığı)
  const requestNumberFromTag =
    selectedAppointment?.tagPrefix ?? selectedAppointment?.requestNumber ?? '';

  const initialValues: AppointmentFormValues = {
    hospitalId: selectedAppointment?.hospitalId || null,
    personId:
      selectedAppointment?.personId != null ? Number(selectedAppointment.personId) : null,
    tagNumberId: selectedAppointment?.tagNumberId ?? null,
    userId: selectedAppointment?.userId ?? null,
    appointmentDate: selectedAppointment?.appointmentDate
      ? new Date(selectedAppointment.appointmentDate).toISOString().split('T')[0]
      : new Date().toISOString().split('T')[0],
    appointmentStartTime: selectedAppointment?.appointmentStartTime || '',
    appointmentEndTime: selectedAppointment?.appointmentEndTime || '',
    deviceList: selectedAppointment?.deviceList || '',
    requestNumber: requestNumberFromTag,
  };

  const userOptions = useMemo((): Array<{ value: string; label: string }> => {
    const opts = users
      .filter(u => u.id && !u.isDeleted)
      .map(u => ({
        value: u.id,
        label: `${u.userName}${u.firstName && u.lastName ? ` (${u.firstName} ${u.lastName})` : ''}`,
      }));

    // Mevcut randevunun kullanıcısı listede yoksa ekle (setlenmesi için)
    const apt = selectedAppointment;
    if (
      apt?.userId &&
      !opts.some(o => String(o.value) === String(apt.userId)) &&
      apt.userName
    ) {
      opts.unshift({ value: apt.userId, label: apt.userName });
    }
    return opts;
  }, [users, selectedAppointment?.userId, selectedAppointment?.userName]);

  const handleSubmit = async (values: AppointmentFormValues) => {
    try {
      if (!selectedAppointment?.id) {
        toast.error('Randevu ID bulunamadı');
        return;
      }

      if (!values.hospitalId || !values.personId) {
        toast.error('Lütfen hastane ve kişi seçiniz');
        return;
      }

      const response = await appointmentStore.updateAppointment(selectedAppointment.id, values);

      if (response.error === false) {
        popupStore.setIsEditAppointmentOpen(false);
        appointmentStore.setSelectedAppointmentToEdit(null);
      }
    } catch (error) {
      console.error('Randevu güncellenirken hata:', error);
      // Error handling is done in the store
    }
  };

  const handleClose = () => {
    popupStore.setIsEditAppointmentOpen(false);
    appointmentStore.setSelectedAppointmentToEdit(null);
    toast.dismiss();
  };

  if (!popupStore.isEditAppointmentOpen || !selectedAppointment) {
    return null;
  }

  return (
    <Formik
      initialValues={initialValues}
      validationSchema={AppointmentSchema}
      onSubmit={handleSubmit}
      enableReinitialize
    >
      {({ values }) => (
        <Popup
          isOpen={popupStore.isEditAppointmentOpen}
          onClose={handleClose}
          title="Randevu Düzenle"
          description="Randevu bilgilerini güncelleyin"
          variant="success"
          icon={<Calendar size={22} strokeWidth={2} />}
          footer={() => (
            <FormPopupFooter
              onClose={handleClose}
              saveLabel="Güncelle"
              submittingLabel="Güncelleniyor..."
            />
          )}
        >
          <Form className={styles.form}>
            <PersonLoader onHospitalChange={handleHospitalChange} />
            <AppointmentEditFormFields
              hospitalOptions={hospitalOptions}
              personOptions={personOptions}
              userOptions={userOptions}
              hospitalId={values.hospitalId}
            />
          </Form>
        </Popup>
      )}
    </Formik>
  );
};

export default observer(EditAppointmentPopup);
